import { PageQuery, Paginate, PaginateMeta } from '@business/entities/common';
import { handleMessage, requestMessage } from '@business/messages';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { R } from '../r';
import { ignoreError } from '../rxjs/operators';
import { LoadingState } from './../repository/loading_state/loading_state';

export type PaginatedCallback<T, E, Q extends PageQuery> = (pageQuery: Q, extraData: E, requestId: () => number) => Observable<Paginate<T>>;

export class PaginatedController<T, E extends any, Q extends PageQuery, A extends any = any> {
  public readonly items$ = new BehaviorSubject<T[]>([]);
  public readonly loading = new LoadingState();
  public readonly pageMeta$ = new BehaviorSubject<PaginateMeta>({
    current_page: 0,
    from: 0,
    last_page: 0,
    per_page: 0,
    to: 0,
    total: 0,
  });

  public readonly additional$ = new BehaviorSubject<A | null>(null);
  public readonly summary$ = new BehaviorSubject<A | null>(null);
  public readonly tooltips$ = new BehaviorSubject<A | null>(null);

  public readonly pageQuery$: BehaviorSubject<Q>;

  private fetchFn: PaginatedCallback<T, E, Q>;
  private extraData: E;

  public onQuery?(query: Q): void;
  private requestId = 1;

  constructor(fetchFn: PaginatedCallback<T, E, Q>, extraData: E, initalQuery: Q | (() => Q)) {
    this.fetchFn = fetchFn;
    this.extraData = extraData;

    this.pageQuery$ = new BehaviorSubject<Q>(R.isFunction(initalQuery) ? initalQuery() : initalQuery);
  }

  fetch(refresh = false) {
    this.requestId += 1;
    return this.fetchFn({ ...this.pageQuery$.value, requestId: this.requestId }, this.extraData, () => this.requestId).pipe(
      tap(result => {
        if (!result) return;
        this.items$.next(result.data);
        this.pageMeta$.next(result.meta);
        this.additional$.next(result.additional || null);
        this.summary$.next(result.summary || null);
        this.tooltips$.next(result.tooltips || null);
      }),
      handleMessage({ error: requestMessage('fetch_error') }),
      ignoreError(),
      this.loading.run({ refresh }),
    );
  }

  reload() {
    return this.fetch(true);
  }

  public patchQuery(query: Partial<Q>, force = false) {
    const oldQuery = this.pageQuery$.value;
    const newQuery = { ...this.pageQuery$.value, ...query };
    this.pageQuery$.next(newQuery);
    this.onQuery && this.onQuery(newQuery);
    if (JSON.stringify(oldQuery) === JSON.stringify(newQuery) && force === false) {
      return of(null);
    }
    return this.fetch();
  }

  public patchQueryWithoutFetch(query: Partial<Q>, force = false) {
    const oldQuery = this.pageQuery$.value;
    const newQuery = { ...this.pageQuery$.value, ...query };
    this.pageQuery$.next(newQuery);
    this.onQuery && this.onQuery(newQuery);
  }

  public updateItems(items: T[]) {
    this.items$.next(items);
  }

  public updateItem(item: T, key: keyof T) {
    const newItems = this.items$.value.map(i => {
      if (i[key] === item[key]) {
        return item;
      }
      return i;
    });
    this.items$.next(newItems);
  }
}
